import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";
import RolePicker from "../RolePicker";
import { startLogin } from "../../utils/session";

function newId() {
  return Math.random().toString(16).slice(2, 10);
}

function StatCard({ label, value }) {
  return (
    <div
      style={{
        padding: "12px",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "8px",
        background: "rgba(0,0,0,0.2)",
      }}
    >
      <div style={{ fontSize: "1.25rem", fontWeight: 600 }}>{value}</div>
      <div style={{ fontSize: "0.75rem", opacity: 0.7, marginTop: 4 }}>
        {label}
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <label
      className="input-label"
      style={{ marginTop: 28, marginBottom: 10, display: "block" }}
    >
      {children}
    </label>
  );
}

function emptyExtras() {
  return {
    embedTemplates: [],
    scheduledPosts: [],
    purgePresets: [],
    ticketDesks: [],
    brandKit: { embedColor: "", welcomeImage: "", leaveImage: "" },
    digest: { channelId: "", hourUtc: 14, enabled: false },
  };
}

export default function HostSection({
  guildId,
  apiBase,
  token,
  channels = [],
  roles = [],
}) {
  const [meta, setMeta] = useState(null);
  const [extras, setExtras] = useState(emptyExtras());
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [needsLogin, setNeedsLogin] = useState(!token);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = () => {
    if (!guildId || !token) return;
    setLoading(true);
    fetch(`${apiBase}/settings/${guildId}/host`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          if (response.status === 401) {
            setNeedsLogin(true);
            throw new Error("Your Discord login expired. Reconnect to continue.");
          }
          throw new Error(payload.error || "Could not load Host tools.");
        }
        setMeta(payload);
        if (payload.extras) {
          setExtras({
            embedTemplates: payload.extras.embedTemplates || [],
            scheduledPosts: payload.extras.scheduledPosts || [],
            purgePresets: payload.extras.purgePresets || [],
            ticketDesks: payload.extras.ticketDesks || [],
            brandKit: {
              embedColor: payload.extras.brandKit?.embedColor || "",
              welcomeImage: payload.extras.brandKit?.welcomeImage || "",
              leaveImage: payload.extras.brandKit?.leaveImage || "",
            },
            digest: {
              channelId: payload.extras.digest?.channelId || "",
              hourUtc: payload.extras.digest?.hourUtc ?? 14,
              enabled: Boolean(payload.extras.digest?.enabled),
            },
          });
        }
        setError("");
      })
      .catch((loadError) => {
        setError(loadError.message || "Could not load Host tools.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!guildId) return undefined;
    if (!token) {
      setNeedsLogin(true);
      setError("Connect your Discord account to view Server Host tools.");
      setLoading(false);
      return undefined;
    }
    setNeedsLogin(false);
    load();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, guildId, token]);

  const save = async ({ applyBrand = false } = {}) => {
    setBusy(applyBrand ? "apply" : "save");
    setStatus("");
    setError("");
    try {
      const response = await fetch(`${apiBase}/settings/${guildId}/host`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          embedTemplates: extras.embedTemplates,
          scheduledPosts: extras.scheduledPosts,
          purgePresets: extras.purgePresets,
          ticketDesks: extras.ticketDesks,
          brandKit: extras.brandKit,
          digest: extras.digest,
          applyBrand,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || "Save failed.");
      }
      if (payload.extras) {
        setExtras((prev) => ({
          ...prev,
          ...payload.extras,
          brandKit: {
            embedColor: payload.extras.brandKit?.embedColor || "",
            welcomeImage: payload.extras.brandKit?.welcomeImage || "",
            leaveImage: payload.extras.brandKit?.leaveImage || "",
          },
          digest: {
            channelId: payload.extras.digest?.channelId || "",
            hourUtc: payload.extras.digest?.hourUtc ?? 14,
            enabled: Boolean(payload.extras.digest?.enabled),
          },
        }));
      }
      setStatus(
        applyBrand
          ? "Saved and applied brand kit to welcome/leave/embed color."
          : "Host settings saved.",
      );
      load();
    } catch (saveError) {
      setError(saveError.message || "Save failed.");
    } finally {
      setBusy("");
    }
  };

  if (needsLogin) {
    return (
      <SettingsSection>
        <p className="help-text">{error}</p>
        <button type="button" className="manage-btn" onClick={() => startLogin()}>
          Connect Discord
        </button>
      </SettingsSection>
    );
  }

  if (loading) {
    return (
      <SettingsSection>
        <p className="help-text">Fetching Host status and insights…</p>
      </SettingsSection>
    );
  }

  if (error && !meta) {
    return (
      <SettingsSection>
        <p className="help-text">{error}</p>
      </SettingsSection>
    );
  }

  const unlocked = meta?.unlocked;
  const stats = meta?.stats;
  const limits = meta?.limits || {};

  if (!unlocked) {
    return (
      <SettingsSection>
        <p className="help-text" style={{ marginBottom: 16 }}>
          Optional one-time add-ons for this server. Core tools stay free — Host
          unlocks templates, multi-desk tickets, schedules, digest, brand kit,
          purge presets, and insights.
        </p>
        <Link
          to="/donate?tier=server_host"
          className="manage-btn"
          style={{
            width: "fit-content",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            padding: "12px 22px",
            boxSizing: "border-box",
          }}
        >
          Unlock Server Host
        </Link>
      </SettingsSection>
    );
  }

  const cardStyle = {
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
    background: "rgba(0,0,0,0.18)",
    display: "grid",
    gap: 8,
  };

  return (
    <SettingsSection>
      <p className="help-text" style={{ marginBottom: 12 }}>
        Manage Server Host add-ons here, or use Discord <code>/host</code>. Kami
        profile is under <strong>Kami Profile</strong> in this sidebar.
      </p>

      {error ? (
        <p className="help-text" style={{ color: "#c97b7b", marginBottom: 12 }}>
          {error}
        </p>
      ) : null}
      {status ? (
        <p className="help-text" style={{ color: "#8fbf8f", marginBottom: 12 }}>
          {status}
        </p>
      ) : null}

      {stats ? (
        <>
          <SectionTitle>INSIGHTS (7 DAYS)</SectionTitle>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
              gap: 10,
            }}
          >
            <StatCard
              label="Members"
              value={stats.memberCount?.toLocaleString?.() || "—"}
            />
            <StatCard
              label="Net growth"
              value={`${stats.net7 >= 0 ? "+" : ""}${stats.net7 ?? 0}`}
            />
            <StatCard label="Joins" value={stats.joins7 ?? 0} />
            <StatCard label="Leaves" value={stats.leaves7 ?? 0} />
            <StatCard
              label="Messages"
              value={stats.messages7?.toLocaleString?.() || 0}
            />
            <StatCard label="Tickets open" value={stats.ticketsOpened ?? 0} />
            <StatCard label="Tickets closed" value={stats.ticketsClosed ?? 0} />
            <StatCard label="Warns" value={stats.mod7?.warn ?? 0} />
            <StatCard label="Kicks" value={stats.mod7?.kick ?? 0} />
            <StatCard label="Bans" value={stats.mod7?.ban ?? 0} />
            <StatCard label="Timeouts" value={stats.mod7?.timeout ?? 0} />
            <StatCard label="Jails" value={stats.mod7?.jail ?? 0} />
          </div>
        </>
      ) : null}

      <SectionTitle>BRAND KIT</SectionTitle>
      <p className="help-text" style={{ marginBottom: 8 }}>
        Store colors/images, then apply them to this server&apos;s embed color and
        welcome/leave images.
      </p>
      <label className="input-label">EMBED COLOR</label>
      <input
        className="settings-input"
        value={extras.brandKit.embedColor || ""}
        placeholder="#4a0e17"
        maxLength={7}
        onChange={(e) =>
          setExtras((prev) => ({
            ...prev,
            brandKit: { ...prev.brandKit, embedColor: e.target.value },
          }))
        }
      />
      <label className="input-label" style={{ marginTop: 12 }}>
        WELCOME IMAGE URL
      </label>
      <input
        className="settings-input"
        value={extras.brandKit.welcomeImage || ""}
        placeholder="https://…"
        onChange={(e) =>
          setExtras((prev) => ({
            ...prev,
            brandKit: { ...prev.brandKit, welcomeImage: e.target.value },
          }))
        }
      />
      <label className="input-label" style={{ marginTop: 12 }}>
        LEAVE IMAGE URL
      </label>
      <input
        className="settings-input"
        value={extras.brandKit.leaveImage || ""}
        placeholder="https://…"
        onChange={(e) =>
          setExtras((prev) => ({
            ...prev,
            brandKit: { ...prev.brandKit, leaveImage: e.target.value },
          }))
        }
      />

      <SectionTitle>
        EMBED TEMPLATES ({extras.embedTemplates.length}/
        {limits.embedTemplates?.host ?? 40})
      </SectionTitle>
      {extras.embedTemplates.map((t, index) => (
        <div key={t.id || index} style={cardStyle}>
          <input
            className="settings-input"
            placeholder="Name"
            value={t.name || ""}
            onChange={(e) => {
              const next = [...extras.embedTemplates];
              next[index] = { ...t, name: e.target.value };
              setExtras((prev) => ({ ...prev, embedTemplates: next }));
            }}
          />
          <input
            className="settings-input"
            placeholder="Title"
            value={t.title || ""}
            onChange={(e) => {
              const next = [...extras.embedTemplates];
              next[index] = { ...t, title: e.target.value };
              setExtras((prev) => ({ ...prev, embedTemplates: next }));
            }}
          />
          <textarea
            className="settings-input"
            placeholder="Description"
            rows={3}
            value={t.description || ""}
            onChange={(e) => {
              const next = [...extras.embedTemplates];
              next[index] = { ...t, description: e.target.value };
              setExtras((prev) => ({ ...prev, embedTemplates: next }));
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input
              className="settings-input"
              placeholder="Color #hex"
              value={t.color || ""}
              onChange={(e) => {
                const next = [...extras.embedTemplates];
                next[index] = { ...t, color: e.target.value };
                setExtras((prev) => ({ ...prev, embedTemplates: next }));
              }}
            />
            <input
              className="settings-input"
              placeholder="Footer"
              value={t.footer || ""}
              onChange={(e) => {
                const next = [...extras.embedTemplates];
                next[index] = { ...t, footer: e.target.value };
                setExtras((prev) => ({ ...prev, embedTemplates: next }));
              }}
            />
          </div>
          <button
            type="button"
            className="manage-btn"
            style={{ width: "fit-content" }}
            onClick={() =>
              setExtras((prev) => ({
                ...prev,
                embedTemplates: prev.embedTemplates.filter((_, i) => i !== index),
              }))
            }
          >
            Remove template
          </button>
        </div>
      ))}
      <button
        type="button"
        className="manage-btn"
        style={{ width: "fit-content" }}
        disabled={
          extras.embedTemplates.length >= (limits.embedTemplates?.host ?? 40)
        }
        onClick={() =>
          setExtras((prev) => ({
            ...prev,
            embedTemplates: [
              ...prev.embedTemplates,
              {
                id: newId(),
                name: "New template",
                title: "",
                description: "",
                color: "",
                footer: "",
              },
            ],
          }))
        }
      >
        Add template
      </button>

      <SectionTitle>
        SCHEDULED POSTS ({extras.scheduledPosts.length}/
        {limits.scheduledPosts?.host ?? 20})
      </SectionTitle>
      <p className="help-text" style={{ marginBottom: 8 }}>
        Posts run around the chosen UTC hour. Pick a saved template and/or add
        text.
      </p>
      {extras.scheduledPosts.map((p, index) => (
        <div key={p.id || index} style={cardStyle}>
          <ChannelPicker
            channels={channels}
            value={p.channelId || ""}
            onChange={(v) => {
              const next = [...extras.scheduledPosts];
              next[index] = { ...p, channelId: v };
              setExtras((prev) => ({ ...prev, scheduledPosts: next }));
            }}
            emptyLabel="Select channel…"
          />
          <select
            className="settings-input"
            value={p.templateId || ""}
            onChange={(e) => {
              const next = [...extras.scheduledPosts];
              next[index] = { ...p, templateId: e.target.value || null };
              setExtras((prev) => ({ ...prev, scheduledPosts: next }));
            }}
          >
            <option value="">No template (text only)</option>
            {extras.embedTemplates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || t.id}
              </option>
            ))}
          </select>
          <textarea
            className="settings-input"
            rows={2}
            placeholder="Optional message content"
            value={p.content || ""}
            onChange={(e) => {
              const next = [...extras.scheduledPosts];
              next[index] = { ...p, content: e.target.value };
              setExtras((prev) => ({ ...prev, scheduledPosts: next }));
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <input
              className="settings-input"
              type="number"
              min={0}
              max={23}
              title="Hour UTC"
              value={p.cronHourUtc ?? 14}
              onChange={(e) => {
                const next = [...extras.scheduledPosts];
                next[index] = { ...p, cronHourUtc: Number(e.target.value) };
                setExtras((prev) => ({ ...prev, scheduledPosts: next }));
              }}
            />
            <select
              className="settings-input"
              value={p.cronDow == null ? "" : String(p.cronDow)}
              onChange={(e) => {
                const next = [...extras.scheduledPosts];
                next[index] = {
                  ...p,
                  cronDow: e.target.value === "" ? null : Number(e.target.value),
                };
                setExtras((prev) => ({ ...prev, scheduledPosts: next }));
              }}
            >
              <option value="">Every day</option>
              <option value="0">Sunday</option>
              <option value="1">Monday</option>
              <option value="2">Tuesday</option>
              <option value="3">Wednesday</option>
              <option value="4">Thursday</option>
              <option value="5">Friday</option>
              <option value="6">Saturday</option>
            </select>
            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="checkbox"
                checked={p.enabled !== false}
                onChange={(e) => {
                  const next = [...extras.scheduledPosts];
                  next[index] = { ...p, enabled: e.target.checked };
                  setExtras((prev) => ({ ...prev, scheduledPosts: next }));
                }}
              />
              Enabled
            </label>
          </div>
          <button
            type="button"
            className="manage-btn"
            style={{ width: "fit-content" }}
            onClick={() =>
              setExtras((prev) => ({
                ...prev,
                scheduledPosts: prev.scheduledPosts.filter((_, i) => i !== index),
              }))
            }
          >
            Remove schedule
          </button>
        </div>
      ))}
      <button
        type="button"
        className="manage-btn"
        style={{ width: "fit-content" }}
        disabled={
          extras.scheduledPosts.length >= (limits.scheduledPosts?.host ?? 20)
        }
        onClick={() =>
          setExtras((prev) => ({
            ...prev,
            scheduledPosts: [
              ...prev.scheduledPosts,
              {
                id: newId(),
                channelId: "",
                templateId: null,
                content: "",
                cronHourUtc: 14,
                cronDow: null,
                enabled: true,
              },
            ],
          }))
        }
      >
        Add schedule
      </button>

      <SectionTitle>STAFF DIGEST</SectionTitle>
      <label style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <input
          type="checkbox"
          checked={Boolean(extras.digest.enabled)}
          onChange={(e) =>
            setExtras((prev) => ({
              ...prev,
              digest: { ...prev.digest, enabled: e.target.checked },
            }))
          }
        />
        <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
          ENABLE DAILY DIGEST
        </span>
      </label>
      <label className="input-label">DIGEST CHANNEL</label>
      <ChannelPicker
        channels={channels}
        value={extras.digest.channelId || ""}
        onChange={(v) =>
          setExtras((prev) => ({
            ...prev,
            digest: { ...prev.digest, channelId: v },
          }))
        }
        emptyLabel="None"
      />
      <label className="input-label" style={{ marginTop: 12 }}>
        HOUR UTC (0–23)
      </label>
      <input
        className="settings-input"
        type="number"
        min={0}
        max={23}
        value={extras.digest.hourUtc ?? 14}
        onChange={(e) =>
          setExtras((prev) => ({
            ...prev,
            digest: { ...prev.digest, hourUtc: Number(e.target.value) },
          }))
        }
      />

      <SectionTitle>
        TICKET DESKS ({extras.ticketDesks.length}/{limits.ticketDesks?.host ?? 8})
      </SectionTitle>
      <p className="help-text" style={{ marginBottom: 8 }}>
        Extra desks beyond the free Support Desk. After saving, post a panel in
        Discord with <code>/host desk panel</code>.
      </p>
      {extras.ticketDesks.map((d, index) => (
        <div key={d.id || index} style={cardStyle}>
          <input
            className="settings-input"
            placeholder="Desk label"
            value={d.label || ""}
            onChange={(e) => {
              const next = [...extras.ticketDesks];
              next[index] = { ...d, label: e.target.value };
              setExtras((prev) => ({ ...prev, ticketDesks: next }));
            }}
          />
          <ChannelPicker
            channels={channels}
            kind="category"
            value={d.categoryId || ""}
            onChange={(v) => {
              const next = [...extras.ticketDesks];
              next[index] = { ...d, categoryId: v };
              setExtras((prev) => ({ ...prev, ticketDesks: next }));
            }}
            emptyLabel="Ticket category…"
          />
          <RolePicker
            roles={roles}
            value={d.staffRoleId || ""}
            onChange={(v) => {
              const next = [...extras.ticketDesks];
              next[index] = { ...d, staffRoleId: v };
              setExtras((prev) => ({ ...prev, ticketDesks: next }));
            }}
            emptyLabel="Staff role…"
          />
          <input
            className="settings-input"
            placeholder="Button label"
            value={d.buttonLabel || ""}
            onChange={(e) => {
              const next = [...extras.ticketDesks];
              next[index] = { ...d, buttonLabel: e.target.value };
              setExtras((prev) => ({ ...prev, ticketDesks: next }));
            }}
          />
          <input
            className="settings-input"
            placeholder="Optional intake form question"
            value={d.formQuestion || ""}
            onChange={(e) => {
              const next = [...extras.ticketDesks];
              next[index] = { ...d, formQuestion: e.target.value || null };
              setExtras((prev) => ({ ...prev, ticketDesks: next }));
            }}
          />
          <label className="input-label">AUTO-CLOSE HOURS (0 = off)</label>
          <input
            className="settings-input"
            type="number"
            min={0}
            max={168}
            value={d.autoCloseHours || 0}
            onChange={(e) => {
              const next = [...extras.ticketDesks];
              next[index] = { ...d, autoCloseHours: Number(e.target.value) };
              setExtras((prev) => ({ ...prev, ticketDesks: next }));
            }}
          />
          <button
            type="button"
            className="manage-btn"
            style={{ width: "fit-content" }}
            onClick={() =>
              setExtras((prev) => ({
                ...prev,
                ticketDesks: prev.ticketDesks.filter((_, i) => i !== index),
              }))
            }
          >
            Remove desk
          </button>
        </div>
      ))}
      <button
        type="button"
        className="manage-btn"
        style={{ width: "fit-content" }}
        disabled={extras.ticketDesks.length >= (limits.ticketDesks?.host ?? 8)}
        onClick={() =>
          setExtras((prev) => ({
            ...prev,
            ticketDesks: [
              ...prev.ticketDesks,
              {
                id: newId(),
                label: "Support",
                categoryId: "",
                staffRoleId: "",
                buttonLabel: "Open Ticket",
                formQuestion: "",
                autoCloseHours: 0,
              },
            ],
          }))
        }
      >
        Add desk
      </button>

      <SectionTitle>
        PURGE PRESETS ({extras.purgePresets.length}/
        {limits.purgePresets?.host ?? 15})
      </SectionTitle>
      <p className="help-text" style={{ marginBottom: 8 }}>
        Saved filters for <code>/host purge run</code> in Discord.
      </p>
      {extras.purgePresets.map((p, index) => (
        <div key={p.id || index} style={cardStyle}>
          <input
            className="settings-input"
            placeholder="Preset name"
            value={p.name || ""}
            onChange={(e) => {
              const next = [...extras.purgePresets];
              next[index] = { ...p, name: e.target.value };
              setExtras((prev) => ({ ...prev, purgePresets: next }));
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <input
              className="settings-input"
              type="number"
              min={1}
              max={100}
              title="Limit"
              value={p.limit || 100}
              onChange={(e) => {
                const next = [...extras.purgePresets];
                next[index] = { ...p, limit: Number(e.target.value) };
                setExtras((prev) => ({ ...prev, purgePresets: next }));
              }}
            />
            <input
              className="settings-input"
              type="number"
              min={1}
              max={336}
              title="Max age hours"
              value={p.maxAgeHours || 24}
              onChange={(e) => {
                const next = [...extras.purgePresets];
                next[index] = { ...p, maxAgeHours: Number(e.target.value) };
                setExtras((prev) => ({ ...prev, purgePresets: next }));
              }}
            />
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={Boolean(p.botsOnly)}
              onChange={(e) => {
                const next = [...extras.purgePresets];
                next[index] = { ...p, botsOnly: e.target.checked };
                setExtras((prev) => ({ ...prev, purgePresets: next }));
              }}
            />
            Bots only
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={Boolean(p.linksOnly)}
              onChange={(e) => {
                const next = [...extras.purgePresets];
                next[index] = { ...p, linksOnly: e.target.checked };
                setExtras((prev) => ({ ...prev, purgePresets: next }));
              }}
            />
            Links only
          </label>
          <button
            type="button"
            className="manage-btn"
            style={{ width: "fit-content" }}
            onClick={() =>
              setExtras((prev) => ({
                ...prev,
                purgePresets: prev.purgePresets.filter((_, i) => i !== index),
              }))
            }
          >
            Remove preset
          </button>
        </div>
      ))}
      <button
        type="button"
        className="manage-btn"
        style={{ width: "fit-content" }}
        disabled={
          extras.purgePresets.length >= (limits.purgePresets?.host ?? 15)
        }
        onClick={() =>
          setExtras((prev) => ({
            ...prev,
            purgePresets: [
              ...prev.purgePresets,
              {
                id: newId(),
                name: "New preset",
                botsOnly: false,
                linksOnly: false,
                maxAgeHours: 24,
                limit: 100,
              },
            ],
          }))
        }
      >
        Add purge preset
      </button>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 10,
          marginTop: 28,
          paddingTop: 16,
          borderTop: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <button
          type="button"
          className="manage-btn"
          disabled={Boolean(busy)}
          onClick={() => save()}
        >
          {busy === "save" ? "Saving…" : "Save Host settings"}
        </button>
        <button
          type="button"
          className="manage-btn"
          disabled={Boolean(busy)}
          onClick={() => save({ applyBrand: true })}
        >
          {busy === "apply" ? "Applying…" : "Save + apply brand kit"}
        </button>
      </div>
    </SettingsSection>
  );
}
